import { DataRoomNode } from 'router/subrouters/DataRoom/models/DataRoomNode';
import { ObjectValues } from 'types';
import { LeadState } from './Lead';

export type WorkflowConfigurationProgression = {
  showInterestPopup: boolean;
  completionAction: StepCompletionAction;
  progressionQuestion?: ProgressionQuestion;
};

export type WorkflowConfiguration = {
  step?: LeadState;

  // Specific rules
  automaticallySendNDA: boolean;
  automaticallySendNDAEnabled: boolean;

  maxCapacity: number;
  maxCapacityEnabled: boolean;

  // Files
  dataRoomDocumentsAvailability: { dataRoomNodeId: string; checked: boolean }[];
} & WorkflowConfigurationProgression;

export type WorkflowTemplateValues = {
  specificRules: Partial<WorkflowConfiguration>;
  maxCapacity?: number;
} & WorkflowConfigurationProgression;

export const FILES_AVAILABILITY_MODE = {
  ALL_AT_ONCE: 'all_at_once',
  ONE_AT_A_TIME: 'one_at_a_time',
} as const;

export type FilesAvailabilityMode = ObjectValues<
  typeof FILES_AVAILABILITY_MODE
>;

export const ORDER_ITEM_TYPE = {
  FILE: 'file',
  MESSAGE: 'message',
} as const;

export type FileOrderItem = {
  type: typeof ORDER_ITEM_TYPE.FILE;
  dataRoomNodeId: string;
};

// Sent in chat with the next file below it, or when the lead reaches the end.
export type MessageOrderItem = {
  type: typeof ORDER_ITEM_TYPE.MESSAGE;
  _id?: string;
  text: string;
};

export type OrderItem = FileOrderItem | MessageOrderItem;

// A message draft keeps a local key until BE gives it an _id.
export type DraftMessageOrderItem = MessageOrderItem & { key: string };

export type OrderFormItem = FileOrderItem | DraftMessageOrderItem;

// The whole stage page is one draft: files, completion setup and stage rules.
export type WorkflowAutomationValues = Omit<
  WorkflowTemplateValues,
  'showInterestPopup'
> & {
  // Ticked files and message steps, in the order leads reach them in "One at a time".
  orderItems: OrderFormItem[];
  filesAvailabilityMode: FilesAvailabilityMode;
};

export type StepWorkflowConfiguration = {
  workflowConfigurationStep: WorkflowConfiguration;
  dataRoomNodes: DataRoomNode[];
  checkedFilesIds: string[];
  orderedItems?: OrderItem[];
  filesAvailabilityMode?: FilesAvailabilityMode;
  // Deal-wide, since the mode applies to all four stages. Paused leads count as part-way.
  partWayLeadsCount?: number;
  // Active leads holding at least one shared file, from automation or by hand.
  leadsWithFilesCount?: number;
  // This stage's files at least one lead has already received.
  receivedFileIds?: string[];
};

export type SaveWorkflowConfigurationPayload =
  Partial<WorkflowConfiguration> & {
    filesAvailabilityMode?: FilesAvailabilityMode;
    orderedItems?: OrderItem[];
  };

export type WorkflowConfigurationSteps = {
  _id: string;
  workflowConfigurationSteps: WorkflowConfiguration[];
  filesAvailabilityMode?: FilesAvailabilityMode;
};

export type WorkflowConfigurationStepsCapacity = Record<LeadState, number>;

export type ProgressionAnswer = {
  _id: string;
  text: string;
  advancesLead: boolean;
};

export type ProgressionQuestion = {
  question: string;
  answers: ProgressionAnswer[];
};

export const STEP_COMPLETION_ACTION = {
  KEEP: 'keep',
  ASK_PROGRESSION_QUESTION: 'ask_progression_question',
  AUTO_ADVANCE: 'auto_advance',
} as const;

export type StepCompletionAction = ObjectValues<typeof STEP_COMPLETION_ACTION>;

export type ReleaseOrderStatus = {
  isPaused: boolean;
  isAwaitingReaction: boolean;
  pausedItem?: { dataRoomNodeId: string; name: string };
  itemsShared: number;
  itemsInOrder: number;
  isLastItem: boolean;
};
